import PWLCalendarComponent from "@/app/components/ui/Calendar/PWLCalendar/index";
import { Dialog, Transition } from "@headlessui/react";
import { Fragment, useEffect, useRef, useState } from "react";

import { ERRORS, IRotaManagementRotaCopyPropsData } from "@/api_framework/api_modals/Rota";
import { useRotaErrorListAPI } from "@/app/hooks/api_hooks/Rota/useRotaErrorListAPI";
import { useRotaShiftCopyAPI } from "@/app/hooks/api_hooks/Rota/useRotaShiftCopyAPI";
import { useRotaShiftFilterAPI } from "@/app/hooks/api_hooks/Rota/useRotaShiftFilterAPI";
import useRouteInfo from "@/app/hooks/useRouteInfo";
import { useRouter } from "@/app/hooks/useRouter";
import { useAppDispatch } from "@/app_redux/hooks/root_hook";
import { IRotaState, setSelectedStafForCopy } from "@/app_redux/reducers/slices/auth/rota_slice";
import { XMarkIcon } from "@heroicons/react/20/solid";
import { addDays, differenceInDays, format, parseISO } from "date-fns";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router";
import { IRotaCopyModal } from "../../interface";

export interface IRotaFiltersFormFields {
  startDate: string;
  endDate: string;
  copyDate: string;
}

const CopyShiftModalComponent: React.FC<IRotaCopyModal> = (props) => {
  const cancelButtonRef = useRef(null);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const fetchErrorListAPI = useRotaErrorListAPI();
  const fetchCliniRotaDetails = useRotaShiftFilterAPI();

  const { getRouteKey } = useRouter();
  const routeId = getRouteKey("ROTA", "id");
  const { routeState } = useRouteInfo(routeId);
  const { RotaFilter, rotaerrorIds } = routeState.state as IRotaState;

  const [open, setOpen] = useState(false);
  const [dateStartTimeEndTime, setDateStartTimeEndTime] = useState(false);
  const [message, setMessage] = useState("");
  const [creationErrorMessage, setCreationErrorMessage] = useState<string | null>("");
  const [daysDiff, setDaysDiff] = useState(0);
  const { execute: fetchRotaShiftCopyAPI, AppError, setAppError, resPonse } = useRotaShiftCopyAPI();

  const handelClick = () => {
    const from_date = formHook.getValues();
    dispatch(setSelectedStafForCopy(props.selectedClinics[0]?.staff_ids));

    const constructedBody: IRotaManagementRotaCopyPropsData = {
      clinic_wise_staffs: props?.selectedClinics,
      copy_from_date_range: {
        start_date: from_date?.startDate,
        end_date: from_date?.endDate,
      },
      copy_to_date: from_date?.copyDate,
      no_of_days_to_copy: daysDiff,
    };
    fetchRotaShiftCopyAPI(constructedBody).then(() => {
      fetchCliniRotaDetails(RotaFilter).then(() => {
        // setOpen(false);
        // props.setCopyActive(false);
      });
    });
  };

  useEffect(() => {
    if (resPonse) {
      setOpen(false);
      props.setCopyActive(false);
    }
  }, [resPonse]);

  const startdate = props.dateRange[0].id;
  const endDate = props.dateRange[6].id;
  const currentDate = new Date(endDate);
  currentDate.setDate(currentDate.getDate() + 1);
  const nextDayValue = currentDate.toISOString().split("T")[0];

  const formHook = useForm<IRotaFiltersFormFields>({
    mode: "onChange",
    defaultValues: { startDate: startdate, endDate: endDate, copyDate: nextDayValue },
  });

  const handelCopyMessageChange = () => {
    const { startDate, endDate, copyDate } = formHook.getValues();

    const formattedStartDate = format(new Date(startDate), "do MMM");
    const formattedEndDate = format(new Date(endDate), "do MMM");
    const formattedCopyDate = format(new Date(copyDate), "do MMM");

    const daysDifference = differenceInDays(parseISO(endDate), parseISO(startDate)) + 1;
    const daysDifferenceForText = differenceInDays(parseISO(endDate), parseISO(startDate));

    // Set the daysDiff state
    setDaysDiff(daysDifference);

    // Create the message
    const copyMessage = `Copying ${formattedStartDate} - ${formattedEndDate} to ${formattedCopyDate} - ${format(addDays(new Date(copyDate), daysDifferenceForText), "do MMM")}`;
    setMessage(copyMessage);
  };

  useEffect(() => {
    handelCopyMessageChange();
  }, []);

  useEffect(() => {
    if (AppError === null) {
      props.setCopyActive(true);
      props.setSelectedClinics([]);
    } else {
      props.setCopyActive(true);
      setCreationErrorMessage(AppError);
    }
  }, [AppError]);

  const handelErrorList = () => {
    const errorId = rotaerrorIds.data.error_id as string;
    const constructedBody: ERRORS = {
      error_id: errorId,
    };
    fetchErrorListAPI(constructedBody);
    navigate(`/app/rota/rota-error`);
  };

  useEffect(() => {
    const formDate = formHook.getValues();
    const start_date = formDate.startDate;
    const end_date = formDate.endDate;
    const startDateObj = new Date(start_date);
    const endDateObj = new Date(end_date);
    if (endDateObj < startDateObj) {
      setDateStartTimeEndTime(true);
    } else {
      setDateStartTimeEndTime(false);
    }
  }, [formHook.watch("startDate"), formHook.watch("endDate")]);
  return (
    <div>
      <button
        disabled={props?.selectedClinics?.length === 0}
        className={`${props?.selectedClinics?.length === 0 ? "opacity-55" : "opacity-100"} bg-[#193A6A] text-white font-semibold text-xs rounded-md py-2.5 px-6`}
        onClick={() => setOpen(true)}
      >
        Copy
      </button>

      <Transition.Root show={open} as={Fragment}>
        <Dialog
          as="div"
          className="fixed inset-0 z-50 overflow-y-auto"
          initialFocus={cancelButtonRef}
          onClose={() => setOpen(false)}
        >
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Dialog.Overlay className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" />
            </Transition.Child>

            {/* This element is to trick the browser into centering the modal contents. */}
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">
              &#8203;
            </span>

            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <div className="inline-block  align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:w-full sm:max-w-lg">
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <h3 className="text-sm text-[#000] font-medium mb-1 flex justify-between">
                    <span>Copy Schedule</span>
                    <span className="ml-6 cursor-pointer" onClick={() => setOpen(false)}>
                      <XMarkIcon className="h-4 w-4" />
                    </span>
                  </h3>
                  <hr />
                  <div className="mt-3">
                    <p>Copy From :</p>
                    <div className="flex gap-8 my-3 items-center">
                      <div>
                        <p>Start Date</p>
                        <PWLCalendarComponent
                          allowPastDaySelection={false}
                          errorMessages={[{ message: "Date is required", type: "required" }]}
                          register={formHook.register("startDate", {
                            required: true,
                          })}
                          showTypedErrors={false}
                          showLeftNav={false}
                          fieldError={formHook.formState.errors.startDate}
                          // disableDatesBeforeDate={startdate}
                          onSelectDate={(date) => {
                            formHook.setValue("startDate", format(date, "yyyy-MM-dd"));
                            handelCopyMessageChange();
                          }}
                          defaultSelectedDate={formHook.watch("startDate")}
                          className=""
                        />
                      </div>
                      <div>
                        <p>End Date</p>
                        <PWLCalendarComponent
                          allowPastDaySelection={false}
                          errorMessages={[{ message: "Date is required", type: "required" }]}
                          register={formHook.register("endDate", {
                            required: true,
                          })}
                          showTypedErrors={false}
                          showLeftNav={false}
                          // disableDatesBeforeDate={formHook.getValues("startDate")}
                          fieldError={formHook.formState.errors.endDate}
                          onSelectDate={(date) => {
                            formHook.setValue("endDate", format(date, "yyyy-MM-dd"));
                            formHook.setValue("copyDate", format(addDays(date, 1), "yyyy-MM-dd"));
                            handelCopyMessageChange();
                          }}
                          defaultSelectedDate={formHook.watch("endDate")}
                          className=""
                        />
                      </div>
                    </div>
                  </div>
                  <hr />
                  {dateStartTimeEndTime && (
                    <p className="text-red-400 text-xs mt-2">End Date cant be greater than the start Date</p>
                  )}
                  <div className="my-4">
                    <p>Copy To :</p>
                    <p className="my-2">Date</p>
                    <div className="w-32">
                      <PWLCalendarComponent
                        allowPastDaySelection={false}
                        errorMessages={[{ message: "Date is required", type: "required" }]}
                        register={formHook.register("copyDate", {
                          required: true,
                        })}
                        disableDatesBeforeDate={formHook.watch("copyDate")}
                        showTypedErrors={false}
                        showLeftNav={false}
                        fieldError={formHook.formState.errors.copyDate}
                        onSelectDate={(date) => {
                          formHook.setValue("copyDate", format(date, "yyyy-MM-dd"));
                          handelCopyMessageChange();
                          setAppError(null);
                        }}
                        defaultSelectedDate={formHook.watch("copyDate")}
                        className=""
                      />
                    </div>
                  </div>
                  <p className="text-[#0068FF]">{message}</p>
                  {creationErrorMessage && (
                    <p className="text-red-500 mt-5">
                      {creationErrorMessage}{" "}
                      {rotaerrorIds?.data && (
                        <span
                          className="text-blue-500 font-medium cursor-pointer ml-4 underline "
                          onClick={handelErrorList}
                        >
                          View Error
                        </span>
                      )}
                    </p>
                  )}
                  <div className="flex gap-4 mt-6 justify-end">
                    <button
                      type="button"
                      onClick={() => {
                        setOpen(false);
                        setAppError(null);
                        setCreationErrorMessage("");
                        props.setCopyActive(false);
                        props.setSelectedClinics([]);
                      }}
                      className="text-xs mt-3 inline-flex w-full justify-center rounded-md bg-[#808DA0] px-4 py-2 font-semibold text-white   ring-inset ring-gray-300 hover-bg-gray-50 sm:mt-0 sm:w-auto"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      disabled={dateStartTimeEndTime}
                      onClick={() => handelClick()}
                      className={`text-xs mt-3 inline-flex w-full justify-center rounded-md ${!dateStartTimeEndTime ? "bg-[#193A6A]" : "bg-gray-400"} px-4 py-2 font-semibold text-white   ring-inset ring-gray-300 hover-bg-gray-50 sm:mt-0 sm:w-auto`}
                    >
                      Save
                    </button>
                  </div>
                </div>
              </div>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition.Root>
    </div>
  );
};

export default CopyShiftModalComponent;
